import type { SagaIterator } from 'redux-saga';
import { put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import { datasetsActions } from 'app-components/flux/datasets';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import * as actions from './reportConfigurationsActions';
import { getLastSyncDate } from './reportConfigurationsSelectors';
import * as transformers from './reportConfigurationsTransformers';
import * as types from './reportConfigurationsTypes';

import type { CoreActionCreator } from '../core/resourceStoreTypes';

export function* reportConfigurationsDumpUpdater(resources: Array<types.ReportConfigurationProps>): SagaIterator {
  const bumpReportConfigurations = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpReportConfigurations');
  if (bumpReportConfigurations) {
    const reportConfigurations = resources.map((resource) => types.mkReportConfiguration(resource));
    yield put(
      actions.getReportConfigurationsSuccess({ resources: reportConfigurations }, { context: 'BUMP_CHANGE_DUMP' }),
    );
  }
}

export function* reportConfigurationsNudgeUpdater(): SagaIterator {
  const bumpReportConfigurations = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpReportConfigurations');
  if (bumpReportConfigurations) {
    yield put(actions.getReportConfigurations(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

const mkQcsSyncReportConfigurationsConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<types.ReportConfigurationProps>();

const mkResourceConfig = (successAction: CoreActionCreator<any, any>, failureAction: CoreActionCreator<any, any>) =>
  mkQcsSyncReportConfigurationsConfig({
    resourceName: 'report-configuration',
    resourceBaseUrl: 'reports/report-configuration',
    getLastSyncDate,
    applyResourceFromCRUDResponse: true,

    transformResponseToResources: transformers.transformResponseToReportConfigurations,
    transformResourceToRequestData: transformers.transformReportConfigurationToRequestData,
    // transformResourcesToRequestData: transformers.transformReportConfigurationsToRequestData,

    successAction,
    failureAction,
  });

export function* getReportConfigurationsActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.getReportConfigurationsSuccess,
    actions.getReportConfigurationsFailure,
  );
  yield resourceSagaUtils.takeAndDebounce(
    actions.getReportConfigurations,
    resourceSagaUtils.qcsSyncGetResources<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* createReportConfigurationActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.createReportConfigurationSuccess,
    actions.createReportConfigurationFailure,
  );
  yield takeLeading(
    actions.createReportConfiguration.type,
    resourceSagaUtils.qcsSyncCreateResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* batchReportConfigurationsActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.batchReportConfigurationsSuccess,
    actions.batchReportConfigurationsFailure,
  );
  yield takeEvery(
    actions.batchReportConfigurations.type,
    resourceSagaUtils.qcsSyncUpdateResources<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* updateReportConfigurationActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.updateReportConfigurationSuccess,
    actions.updateReportConfigurationFailure,
  );
  yield takeLatest(
    actions.updateReportConfiguration.type,
    resourceSagaUtils.qcsSyncUpdateResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* deleteReportConfigurationActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.deleteReportConfigurationSuccess,
    actions.deleteReportConfigurationFailure,
  );
  yield takeEvery(
    actions.deleteReportConfiguration.type,
    resourceSagaUtils.qcsSyncDeleteResource<types.ReportConfigurationProps>,
    resourceConfig,
  );
}

function* getReportConfigurations() {
  yield put(actions.getReportConfigurations());
}

function* datasetPollSuccessActionWatcher() {
  yield takeEvery(datasetsActions.datasetPollSuccess, getReportConfigurations);
}

function* reportConfigurationFailureActionWatcher() {
  yield takeEvery(
    [
      actions.createReportConfigurationFailure.type,
      actions.updateReportConfigurationFailure.type,
      actions.deleteReportConfigurationFailure.type,
      actions.batchReportConfigurationsFailure.type,
    ],
    getReportConfigurations,
  );
}

export default [
  getReportConfigurationsActionWatcher,
  createReportConfigurationActionWatcher,
  batchReportConfigurationsActionWatcher,
  updateReportConfigurationActionWatcher,
  deleteReportConfigurationActionWatcher,
  // generateReportActionWatcher,
  datasetPollSuccessActionWatcher,
  reportConfigurationFailureActionWatcher,
];
